import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { BreakdownImportModel } from 'src/app/models/imports/breakdown-import';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { BreakdownByDetailModel } from 'src/app/models/preliminaries/breakdown-by-detail-model';
import { BreakdownModel } from 'src/app/models/preliminaries/breakdown-model';
import { ExportQuotationDetailResponseModel } from 'src/app/models/preliminaries/export-quotation-detail-response.model';
import { QuotationDetailModel } from 'src/app/models/preliminaries/quotation-detail.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { BreakdownByDetailTableModel } from 'src/app/models/tables/preliminaries/breakdown-by-detail-table.model';
import { QuotationBreakdownCaptureTableModel } from 'src/app/models/tables/preliminaries/quotation-breakdown-capture-table.model';
import { QuotationBreakdownTableModel } from 'src/app/models/tables/preliminaries/quotation-breakdown-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class BreakdownService {

  constructor(private _httpClient: HttpClient) { }

  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams()
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.ExportDetailConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  exportSolution(quotationDetailId: number): Observable<Blob> {
    return this._httpClient.get(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.ExportSolution}/${quotationDetailId}`, {
      responseType: 'blob',
    });
  }

  getAllBreakdownsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<QuotationBreakdownTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<QuotationBreakdownTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetAllBreakdownsWithPagination}`,
      filters, { params: params }
    );
  }

  getAllBreakdownsByQuotationForCapture(quotationId: number, quotationDetailId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean): Observable<PagedResponseModel<QuotationBreakdownCaptureTableModel>> {
    const params = new HttpParams()
      .set('quotationId', quotationId)
      .set('quotationDetailId', quotationDetailId)
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);
    return this._httpClient.get<PagedResponseModel<QuotationBreakdownCaptureTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetAllBreakdownsByQuotationForCapture}`,
      { params: params }
    );
  }

  getColumnsForDetailConsultationAsync(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetColumnsForDetailConsultation}`);
  }

  getColumnsForBreakdownCapture(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetColumnsForBreakdownCapture}`);
  }

  getBreakdownById(breakdownId: number): Observable<BreakdownModel> {
    return this._httpClient.get<BreakdownModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetBreakdownById}/${breakdownId}`);
  }

  createBreakdown(breakdownContract: BreakdownModel): Observable<BreakdownModel> {
    return this._httpClient.post<BreakdownModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.CreateBreakdown}`,
      breakdownContract);
  }

  createBreakdownFromMasterSolutionAsync(quotationVersionId: number, quotationDetailId: number | null, masterSolutionId: number, compositeArticleDetailIds: number[]): Observable<QuotationDetailModel> {
    const params = new HttpParams().set('masterSolutionId', masterSolutionId).set('quotationDetailId', quotationDetailId || 0)
    return this._httpClient.post<QuotationDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.CreateBreakdownFromMasterSolution}/${quotationVersionId}`,
      compositeArticleDetailIds, { params: params }
    );
  }

  createMultipleBreakdowns(listbreakdowns: Array<BreakdownModel>, quotationDetailId: number): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.CreateMultipleBreakdowns}/${quotationDetailId}`,
      listbreakdowns);
  }

  updateBreakdown(breakdownContract: BreakdownModel): Observable<BreakdownModel> {
    return this._httpClient.put<BreakdownModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.UpdateBreakdown}`,
      breakdownContract);
  }

  deleteBreakdown(breakdownId: number): Observable<BreakdownModel> {
    return this._httpClient.delete<BreakdownModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.DeleteBreakdown}/${breakdownId}`);
  }

  deleteSomeDetails(breakdownIds: Array<number>): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.DeleteSomeDetails}`,
      breakdownIds);
  }

  getBreakdownByQuotationDetailId(quotationDetailId: number): Observable<Array<BreakdownModel>> {
    return this._httpClient.get<Array<BreakdownModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetBreakdownByQuotationDetailId}/${quotationDetailId}`);
  }

  getTotalBreakdownByQuotationDetailId(quotationDetailId: number): Observable<number> {
    return this._httpClient.get<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetTotalBreakdownByQuotationDetailId}/${quotationDetailId}`);
  }

  applyDiscount(quotationDetailId: number, discountRate: number, familyId: number | null, groupId: number | null, subgroupId: number | null): Observable<boolean> {
    const params = new HttpParams()
      .set('familyId', familyId || 0)
      .set('groupId', groupId || 0)
      .set('subgroupId', subgroupId || 0)

    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.ApplyDiscount}/${quotationDetailId}`,
      discountRate, { params: params });
  }

  totalValueDistribute(quotationDetailId: number, valueDistribute: number): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.TotalValueDistribute}/${quotationDetailId}`,
      valueDistribute);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.ExportImportErrors}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  importBreakdownAsync(orderImports: Array<BreakdownImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.ImportBreakdownAsync}`,
      orderImports);
  }

  getBreakdownByQuotationDetailToExport(quotationDetailId: number): Observable<Array<ExportQuotationDetailResponseModel>> {
    return this._httpClient.get<Array<ExportQuotationDetailResponseModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetBreakdownByQuotationDetailToExportAsync}/${quotationDetailId}`);
  }

  getBreakdownByDetail(filter: BreakdownByDetailModel): Observable<Array<BreakdownByDetailModel>> {
    return this._httpClient.post<Array<BreakdownByDetailModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetBreakdownByDetail}`,
      filter);
  }

  getBreakdownByDetailId(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[], quotationDetailId: number, quotationId: number, orderId: number, internalOrderId: number = 0): Observable<PagedResponseModel<BreakdownByDetailTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('quotationDetailId', quotationDetailId).set('quotationId', quotationId).set('orderId', orderId).set('internalOrderId', internalOrderId);
    return this._httpClient.post<PagedResponseModel<BreakdownByDetailTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetBreakdownByDetailIdAsync}`,
      filters, { params: params }
    );
  }

  getColumnsBreakdownByDetailId(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.breakdown.GetColumnsBreakdownByDetailIdAsync}`);
  }
}
